import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  Tab,
  Tabs,
  LinearProgress,
  Fade,
} from "@mui/material";
import {
  ShoppingCart,
  AttachMoney,
  Inventory,
  People,
  Add,
  Refresh,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../services/supabase";
import { DashboardStats, Order, Product } from "../../types";
import { useNavigate } from "react-router-dom";
import { productService } from "../../services/products";
import { useTranslation } from "react-i18next";
import SellerReviews from "./SellerReviews";
import StatCard from "./dashboard/components/StatCard";
import OverviewTab from "./dashboard/components/OverviewTab";
import RecentOrdersTab from "./dashboard/components/RecentOrdersTab";
import ProductsTab from "./dashboard/components/ProductsTab";
import AnalyticsTab from "./dashboard/components/AnalyticsTab";

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<DashboardStats>({
    total_sales: 0,
    total_orders: 0,
    pending_orders: 0,
    total_revenue: 0,
    commission_earned: 0,
  });
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDashboardStats();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && activeTab === 1) {
      fetchOrders();
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (user && activeTab === 2) {
      fetchProducts();
    }
  }, [user, activeTab]);

  const fetchDashboardStats = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch orders for this seller
      const { data: orders, error: ordersError } = await supabase
        .from("orders")
        .select("*")
        .eq("seller_id", user.id);

      if (ordersError) throw ordersError;

      // Calculate statistics
      const totalOrders = orders?.length || 0;
      const pendingOrders =
        orders?.filter((order) => order.status === "pending").length || 0;
      const totalRevenue =
        orders?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;
      const commissionEarned =
        orders?.reduce(
          (sum, order) => sum + (order.commission_amount || 0),
          0
        ) || 0;

      // Fetch products count
      const { count: productsCount } = await supabase
        .from("products")
        .select("*", { count: "exact", head: true })
        .eq("seller_id", user.id);

      setStats({
        total_sales: productsCount || 0,
        total_orders: totalOrders,
        pending_orders: pendingOrders,
        total_revenue: totalRevenue,
        commission_earned: commissionEarned,
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    if (!user?.id) return;

    setOrdersLoading(true);
    try {
      const { data: orders, error } = await supabase
        .from("orders")
        .select(
          `
          *,
          items:order_items(
            *,
            product:products(*)
          )
        `
        )
        .eq("seller_id", user.id)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      setOrders(orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchProducts = async () => {
    if (!user?.id) return;

    setProductsLoading(true);
    try {
      const { data, error } = await productService.getProductsBySeller(user.id);
      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "#ffaa00";
      case "confirmed":
        return "#00d4ff";
      case "shipped":
        return "#00d4ff";
      case "delivered":
        return "#00ff88";
      case "cancelled":
        return "#ff3366";
      default:
        return "#888888";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-EU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const { t, i18n } = useTranslation();

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm(t("dashboard.deleteProductConfirm"))) {
      try {
        const { error } = await productService.deleteProduct(productId);
        if (error) throw error;

        // Refresh products list
        fetchProducts();
        fetchDashboardStats();
      } catch (error) {
        console.error("Error deleting product:", error);
        alert(t("dashboard.failedToDeleteProduct"));
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Fade in timeout={600}>
        <Box>
          {/* Header */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 0 },
              justifyContent: { xs: "flex-start", md: "space-between" },
              alignItems: { xs: "stretch", md: "center" },
            }}
          >
            <Box>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  fontWeight: 800,
                  background:
                    "linear-gradient(135deg, #fa0c83 0%, #0bd5fd 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("dashboard.sellerDashboard")}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                {user
                  ? t("dashboard.welcomeBack", {
                      name: user?.profile?.full_name || user?.email,
                    })
                  : t("dashboard.welcomeToPortal")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: { xs: "stretch", md: "flex-end" },
              }}
            >
              <IconButton
                onClick={fetchDashboardStats}
                sx={{
                  bgcolor: "rgba(0,212,255,0.1)",
                  "&:hover": { bgcolor: "rgba(0,212,255,0.2)" },
                }}
              >
                <Refresh />
              </IconButton>
              {!user && (
                <Button
                  variant="contained"
                  onClick={() => navigate("/register")}
                  fullWidth
                  sx={{
                    background:
                      "linear-gradient(135deg, #fe0c85 0%, #cc0066 100%)",
                    boxShadow: "0 2px 8px rgba(255,0,128,0.2)",
                    flex: { xs: "1 1 100%", sm: "0 1 auto" },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 3px 12px rgba(255,0,128,0.25)",
                    },
                  }}
                >
                  {t("dashboard.registerAsSeller")}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate("/admin/products/new")}
                disabled={!user}
                fullWidth
                sx={{
                  background:
                    "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)",
                  boxShadow: "0 2px 8px rgba(0,212,255,0.2)",
                  flex: { xs: "1 1 100%", sm: "0 1 auto" },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 3px 12px rgba(0,212,255,0.25)",
                  },
                }}
              >
                {t("dashboard.addProduct")}
              </Button>
            </Box>
          </Box>

          {/* Stats Grid using Box */}
          {loading ? (
            <LinearProgress sx={{ mb: 4 }} />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 3,
                mb: 4,
              }}
            >
              <StatCard
                title={t("dashboard.totalProducts")}
                value={stats.total_sales}
                icon={<Inventory />}
                color="#00d4ff"
                delay={0}
              />
              <StatCard
                title={t("dashboard.totalOrders")}
                value={stats.total_orders}
                icon={<ShoppingCart />}
                trend={12}
                color="#ff0080"
                delay={100}
              />
              <StatCard
                title={t("dashboard.pendingOrders")}
                value={stats.pending_orders}
                icon={<People />}
                color="#ffaa00"
                delay={200}
              />
              <StatCard
                title={t("dashboard.revenue")}
                value={`€${stats.total_revenue.toFixed(2)}`}
                icon={<AttachMoney />}
                trend={8}
                color="#00ff88"
                delay={300}
              />
            </Box>
          )}

          {/* Main Content Tabs */}
          <Paper
            sx={{
              bgcolor: "background.paper",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                "& .MuiTab-root": {
                  fontWeight: 600,
                  minWidth: { xs: "auto", sm: 90 },
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  "&.Mui-selected": {
                    color: "#00d4ff",
                  },
                },
                "& .MuiTabs-indicator": {
                  bgcolor: "#00d4ff",
                  height: 3,
                },
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab label={t("dashboard.tabs.overview")} />
              <Tab label={t("dashboard.tabs.recentOrders")} />
              <Tab label={t("dashboard.tabs.products")} />
              <Tab label={t("dashboard.tabs.analytics")} />
              <Tab label={t("dashboard.tabs.reviews")} />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {activeTab === 0 && <OverviewTab stats={stats} />}

              {activeTab === 1 && (
                <RecentOrdersTab orders={orders} loading={ordersLoading} />
              )}

              {activeTab === 2 && (
                <ProductsTab
                  products={products}
                  loading={productsLoading}
                  onDelete={handleDeleteProduct}
                />
              )}

              {activeTab === 3 && (
                <AnalyticsTab stats={stats} products={products} />
              )}
              {activeTab === 4 && (
                <Box>
                  <SellerReviews />
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Container>
  );
};

export default AdminDashboard;
